import type { TypeId } from '@unipile/db';
import type {
  ChallengeSelectionCheckpoint,
  ProviderAuthenticationIntentResponse,
  RequestCheckpointChallenge,
} from '@unipile/types';
import { useId } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRequestCheckpoint } from '../../../features/accounts/api/request-checkpoint';
import { useProvider } from '../../../hooks/useProvider';
import { Button } from '../../shadcn/Button';
import { Field, FieldGroup } from '../../shadcn/Field';
import { Item, ItemContent, ItemDescription, ItemTitle } from '../../shadcn/Item';
import { RadioGroup, RadioGroupItem } from '../../shadcn/RadioGroup';
import { Spinner } from '../../shadcn/Spinner';
import { WizardStepContainer } from '../containers/WizardStepContainer';

type ChallengeSelectionForm = { challenge: RequestCheckpointChallenge };

/**
 * A checkpoint that lets the user pick which alternative verification method to use.
 *
 * The chosen option's `id` (its channel: EMAIL/SMS/WHATSAPP or any agnostic ID) is submitted as `challenge` through
 * request-checkpoint — not solve-checkpoint. The provider then runs the actual channel switch and
 * returns the next checkpoint (OTP / in-app validation).
 */
export function ChallengeSelectionCheckpoint({
  checkpoint,
  onSuccess,
  onError,
  intent_id,
}: {
  onSuccess: (response: ProviderAuthenticationIntentResponse) => void;
  onError: (error: unknown) => void;
  checkpoint: ChallengeSelectionCheckpoint;
  intent_id: TypeId<'account'>;
}) {
  const { t } = useTranslation();
  const { provider } = useProvider();
  const radioGroupId = useId();

  const form = useForm<ChallengeSelectionForm>({
    defaultValues: { challenge: checkpoint.challenges[0]?.id },
  });

  const { mutate, isPending } = useRequestCheckpoint({
    mutationConfig: { onSuccess, onError },
  });

  const onSubmit = (data: ChallengeSelectionForm) => {
    mutate({ data: { ...data, intent_id } });
  };

  return (
    <WizardStepContainer
      title={t('CHALLENGE_SELECTION.title', { ns: provider })}
      description={t('CHALLENGE_SELECTION.description', { ns: provider })}
    >
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FieldGroup>
          <Controller
            control={form.control}
            name="challenge"
            rules={{ required: true }}
            render={({ field }) => (
              <Field>
                <RadioGroup value={field.value} onValueChange={field.onChange} className="gap-2">
                  {checkpoint.challenges.map(({ id, label, description }, index) => {
                    const inputId = `${radioGroupId}-${index}`;

                    return (
                      <Item key={id} asChild variant="outline">
                        <label
                          htmlFor={inputId}
                          className="has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:ring-primary/30 cursor-pointer has-[[data-state=checked]]:ring-2"
                        >
                          <RadioGroupItem value={id} id={inputId} />
                          <ItemContent>
                            <ItemTitle>{label}</ItemTitle>
                            {description && <ItemDescription>{description}</ItemDescription>}
                          </ItemContent>
                        </label>
                      </Item>
                    );
                  })}
                </RadioGroup>
              </Field>
            )}
          />

          <Button className="mt-8 w-full" type="submit" disabled={isPending}>
            {isPending && <Spinner />}
            {t('Common.next')}
          </Button>
        </FieldGroup>
      </form>
    </WizardStepContainer>
  );
}
